import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ImageLightboxService } from 'src/app/services/image-lightbox.service';

@Component({
  selector: 'app-image-lightbox',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    @if (lightbox.visible()) {
      <div
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
        (click)="onBackdropClick($event)"
        role="dialog"
        aria-modal="true"
        aria-label="View image"
      >
        <button
          pButton
          type="button"
          icon="pi pi-times"
          class="p-button-rounded p-button-text absolute top-4 right-4 z-10 text-white hover:bg-white/20"
          (click)="lightbox.close()"
          aria-label="Close"
        ></button>

        @if (lightbox.canGoPrev()) {
          <button
            pButton
            type="button"
            icon="pi pi-chevron-left"
            class="p-button-rounded p-button-text absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
            (click)="goPrev($event)"
            aria-label="Previous image"
          ></button>
        }

        @if (lightbox.currentImage(); as src) {
          <img
            [src]="src"
            alt=""
            class="max-w-[90vw] max-h-[90vh] w-auto h-auto object-contain"
            (click)="$event.stopPropagation()"
          />
        }

        @if (lightbox.canGoNext()) {
          <button
            pButton
            type="button"
            icon="pi pi-chevron-right"
            class="p-button-rounded p-button-text absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
            (click)="goNext($event)"
            aria-label="Next image"
          ></button>
        }

        @if (lightbox.images().length > 1) {
          <div class="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm">
            {{ lightbox.currentIndex() + 1 }} / {{ lightbox.images().length }}
          </div>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageLightboxComponent {
  lightbox = inject(ImageLightboxService);

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (!this.lightbox.visible()) return;
    if (e.key === 'Escape') {
      this.lightbox.close();
    } else if (e.key === 'ArrowLeft') {
      this.lightbox.prev();
    } else if (e.key === 'ArrowRight') {
      this.lightbox.next();
    }
  }

  onBackdropClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).getAttribute('role') === 'dialog') {
      this.lightbox.close();
    }
  }

  goPrev(e: MouseEvent): void {
    e.stopPropagation();
    this.lightbox.prev();
  }

  goNext(e: MouseEvent): void {
    e.stopPropagation();
    this.lightbox.next();
  }
}
